/* How the book divides on the Value split: shares, futures, and the cash that
 * is actually spare.
 *
 * `totals.cashUsd` counts every free balance, and the unposted balance on a
 * futures wallet is one of them. For this picture that puts it on the wrong
 * side. Money parked on the exchange is there to back contracts, so it reads as
 * committed alongside the margin rather than as cash waiting to be spent — and
 * with no contract open, the old slice (margin only) disappeared while the
 * wallet still held the money.
 *
 * The futures pillar is the whole wallet: margin, what the contracts are
 * showing, and the rest of the balance — the figure Leverage calls what is on
 * the exchange. The parts still add up to the book. The totals themselves are
 * not touched: the rules and the history go on reading `cashUsd` as before. */

import type { BookTotals } from '../types';

export interface BookSplit {
  sharesUsd: number;
  sharesPct: number;
  futuresUsd: number;
  futuresPct: number;
  /** Free balances outside any futures wallet. Negative when those are. */
  spareCashUsd: number;
  spareCashPct: number;
  /** Shares and futures together: the money that is not spare. */
  committedUsd: number;
  committedPct: number;
}

export function bookSplit(
  t: Pick<BookTotals, 'equityUsd' | 'cashUsd' | 'futuresFreeUsd' | 'futuresWalletUsd' | 'totalUsd'>,
): BookSplit {
  const share = (usd: number) => (t.totalUsd > 0 ? (usd / t.totalUsd) * 100 : 0);
  const spareCashUsd = t.cashUsd - t.futuresFreeUsd;
  const committedUsd = t.equityUsd + t.futuresWalletUsd;
  return {
    sharesUsd: t.equityUsd,
    sharesPct: share(t.equityUsd),
    futuresUsd: t.futuresWalletUsd,
    futuresPct: share(t.futuresWalletUsd),
    spareCashUsd,
    spareCashPct: share(spareCashUsd),
    committedUsd,
    committedPct: share(committedUsd),
  };
}
